import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { Section, SectionHeader } from '@/components/ui/Section';
import { AuthButton } from '@/components/ui/AuthButton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { HandDrawnCheck } from '@/components/ui/decorative';
import { cn } from '@/lib/utils';
import { usePricing } from '@/features/pricing/api/pricingQueries';

interface PricingCardProps {
  title: string;
  price: number;
  currencySymbol: string;
  features: string[];
  popular?: boolean;
  subtext?: string;
  onCTAClick: () => void;
}

function PricingCard({ title, price, currencySymbol, features, popular = false, subtext, onCTAClick }: PricingCardProps): React.ReactElement {
  return (
    <Card className={cn(
      "flex flex-col h-full transition-all duration-300 paper-edge",
      popular ? "bg-charcoal text-white shadow-2xl z-10 border-charcoal" : "bg-pressed-paper border-champagne/30"
    )}>
      <CardHeader className="relative">
        {popular && (
          <Badge variant="terracotta" className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 shadow-lg bg-white text-bottle-green">
            Most Popular
          </Badge>
        )}
        <CardTitle className={cn("text-xl mb-2 font-display font-semibold", popular ? "text-white" : "text-charcoal")}>{title}</CardTitle>
        <div className="flex items-baseline gap-1">
          <span className={cn(
            "font-display font-bold tabular-nums tracking-tight leading-none",
            "text-[clamp(2.25rem,3.5vw+1rem,3rem)]",
            popular ? "text-white" : "text-charcoal"
          )}>
            {currencySymbol}{price.toFixed(2)}
          </span>
          <span className={cn("text-sm", popular ? "text-white/60" : "text-muted-warm")}>/ article</span>
        </div>
        {subtext ? (
          <p className="text-sm font-medium text-champagne mt-2">{subtext}</p>
        ) : (
          <div className="mt-2 h-5"></div>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {features.map((feature) => (
          <div key={feature} className={cn("flex items-start gap-3 text-sm", popular ? "text-white/80" : "text-muted-warm")}>
            <HandDrawnCheck className="mt-0.5" />
            <span className="font-medium">{feature}</span>
          </div>
        ))}
      </CardContent>

      <CardFooter>
        <AuthButton
          variant={popular ? 'terracotta' : 'outline'}
          className={cn(
            "w-full rounded-lg",
            popular
              ? "bg-bottle-green text-white hover:bg-sage"
              : "border-charcoal text-charcoal hover:bg-charcoal hover:text-white"
          )}
          onAuthenticatedClick={onCTAClick}
        >
          {popular ? 'Get Started' : 'Buy Now'}
        </AuthButton>
      </CardFooter>
    </Card>
  );
}

export function Pricing(): React.ReactElement {
  const navigate = useNavigate();
  const { data: pricingConfig } = usePricing();

  const basePrice = pricingConfig?.articleBasePrice ?? 49;
  const currencySymbol = pricingConfig?.currencySymbol ?? '$';

  const DISCOUNT_10_PACK = 0.2;
  const DISCOUNT_25_PACK = 0.29;

  function handleCTAClick(): void {
    navigate('/research/deep');
  }

  return (
    <Section className="bg-cream-paper" id="pricing">
      <SectionHeader
        badge="Pricing"
        title="Pay only for articles you create"
        description="No subscriptions, no waste. Credits never expire."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-center">
        <PricingCard
          title="Single Article"
          price={basePrice}
          currencySymbol={currencySymbol}
          features={[
            "Full AI research",
            "Writing & Citations",
            "Export to Word/Markdown",
            "No expiration"
          ]}
          onCTAClick={handleCTAClick}
        />
        <PricingCard
          title="The Edition"
          price={basePrice * (1 - DISCOUNT_10_PACK)}
          currencySymbol={currencySymbol}
          subtext={`Save ${currencySymbol}${(basePrice * DISCOUNT_10_PACK * 10).toFixed(0)} total`}
          popular={true}
          features={[
            "Everything in Single",
            "Volume discount",
            "90-day active window",
            "Priority generation"
          ]}
          onCTAClick={handleCTAClick}
        />
        <PricingCard
          title="The Agency"
          price={basePrice * (1 - DISCOUNT_25_PACK)}
          currencySymbol={currencySymbol}
          subtext={`Save ${currencySymbol}${(basePrice * DISCOUNT_25_PACK * 25).toFixed(0)} total`}
          features={[
            "Everything in Single",
            "Maximum savings",
            "180-day active window",
            "Dedicated support"
          ]}
          onCTAClick={handleCTAClick}
        />
      </div>

      <p className="mt-12 text-center text-muted-warm text-sm">
        First article free with full features. No credit card required for trial.
      </p>
    </Section>
  );
}
